import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Sidebar from "../../menu/Sidebar";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./Store.css";
import { featuredProducts, promotionProducts } from "./data/storeCatalog";

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const fallbackProducts = [...promotionProducts, ...featuredProducts];

const normalizeProduct = (product) => ({
  ...product,
  title: product.title || product.name,
  description: product.description || product.subtitle || "",
  categoryLabel: product.categoryName || product.category || "",
  currentPrice:
    product.promotionalPrice !== null && product.promotionalPrice !== undefined
      ? product.promotionalPrice
      : product.price,
  originalPrice:
    product.originalPrice ??
    (product.promotionalPrice !== null && product.promotionalPrice !== undefined
      ? product.price
      : null),
  images: Array.isArray(product.images) ? product.images : [],
});

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const StoreProductDetail = () => {
  const { id } = useParams();
  const [menuClose, setMenuClose] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [message, setMessage] = useState("");

  const toggleMenu = () => {
    setMenuClose((prev) => !prev);
  };

  useEffect(() => {
    let mounted = true;

    const loadProduct = async () => {
      try {
        setLoading(true);
        const response = await Api.get("/public/store-products");
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];
        if (!mounted) return;
        setProducts(items.length > 0 ? items : fallbackProducts);
      } catch (err) {
        console.error("Erro ao carregar produto da store:", err);
        if (mounted) setProducts(fallbackProducts);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadProduct();

    return () => {
      mounted = false;
    };
  }, []);

  const product = useMemo(() => {
    const found = products.find((item) => String(item.id) === String(id));
    return found ? normalizeProduct(found) : null;
  }, [id, products]);

  useEffect(() => {
    setActiveImage(0);
    setMessage("");
  }, [id]);

  const images = product?.images || [];
  const currentPrice = toFiniteNumber(product?.currentPrice);
  const originalPrice = toFiniteNumber(product?.originalPrice);
  const points = toFiniteNumber(product?.points);
  const totalPrice = currentPrice === null ? null : currentPrice * quantity;
  const totalPoints = points === null ? null : points * quantity;
  const hasOriginalPrice =
    originalPrice !== null && currentPrice !== null && originalPrice > currentPrice;

  const changeQuantity = (nextValue) => {
    const nextQuantity = Math.max(1, Math.min(999, Number(nextValue) || 1));
    setQuantity(nextQuantity);
    setMessage("");
  };

  const addToCart = () => {
    if (!product) return;

    const cartItem = {
      id: product.id,
      title: product.title,
      quantity,
      price: currentPrice,
      points,
      imageUrl: images[0]?.url || "",
      addedAt: new Date().toISOString(),
    };

    try {
      const currentCart = JSON.parse(window.localStorage.getItem("smartStoreCart") || "[]");
      const cart = Array.isArray(currentCart) ? currentCart : [];
      const existingIndex = cart.findIndex((item) => String(item.id) === String(product.id));

      if (existingIndex >= 0) {
        cart[existingIndex] = {
          ...cart[existingIndex],
          quantity: Number(cart[existingIndex].quantity || 0) + quantity,
          addedAt: cartItem.addedAt,
        };
      } else {
        cart.push(cartItem);
      }

      window.localStorage.setItem("smartStoreCart", JSON.stringify(cart));
      setMessage("Produto adicionado ao carrinho.");
    } catch (_err) {
      setMessage("Produto selecionado. Nao foi possivel salvar o carrinho neste navegador.");
    }
  };

  return (
    <div className="app-container">
      <Sidebar close={menuClose} storeActive="active" />

      <main className="main-content">
        <Topbar page="Store / Produto" onToggleMenu={toggleMenu} />

        <div className="store-page">
          <Link to="/store" className="store-product-detail-back">
            Voltar para a Store
          </Link>

          {loading ? (
            <section className="store-product-detail-empty">Carregando produto...</section>
          ) : !product ? (
            <section className="store-product-detail-empty">
              Produto nao encontrado.
              <Link to="/store">Ver produtos da Store</Link>
            </section>
          ) : (
            <section className="store-product-detail">
              <div className="store-product-detail-gallery">
                <div
                  className={`store-product-detail-image ${images.length > 0 ? "has-images" : `tone-${product.tone || "midnight"}`}`}
                >
                  {images[activeImage]?.url ? (
                    <img src={images[activeImage].url} alt={product.title} />
                  ) : (
                    <div className="store-product-art">
                      <strong>{product.artLabel || product.title}</strong>
                      {product.artSubLabel ? <span>{product.artSubLabel}</span> : null}
                    </div>
                  )}
                </div>

                {images.length > 1 ? (
                  <div className="store-product-detail-thumbs">
                    {images.map((image, index) => (
                      <button
                        type="button"
                        key={image.id || `${product.id}-${index}`}
                        className={index === activeImage ? "is-active" : ""}
                        onClick={() => setActiveImage(index)}
                        aria-label={`Exibir foto ${index + 1}`}
                      >
                        <img src={image.url} alt={`${product.title} ${index + 1}`} />
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="store-product-detail-info">
                <span className="store-section-eyebrow">{product.categoryLabel || "Store"}</span>
                <h1>{product.title}</h1>
                {product.description ? <p>{product.description}</p> : null}

                <div className="store-product-detail-pricing">
                  {currentPrice !== null ? (
                    <strong>{currencyFormatter.format(currentPrice)}</strong>
                  ) : null}
                  {hasOriginalPrice ? <span>{currencyFormatter.format(originalPrice)}</span> : null}
                  {points !== null ? <small>{points} pontos por unidade</small> : null}
                </div>

                <div className="store-product-detail-meta">
                  {product.minOrder ? <span>{product.minOrder}</span> : null}
                  {product.delivery ? <span>{product.delivery}</span> : null}
                  {images.length > 0 ? <span>{images.length} foto(s)</span> : null}
                </div>

                <div className="store-product-detail-buy">
                  <label>
                    Quantidade
                    <div className="store-product-quantity">
                      <button
                        type="button"
                        onClick={() => changeQuantity(quantity - 1)}
                        disabled={quantity <= 1}
                      >
                        -
                      </button>
                      <input
                        value={quantity}
                        inputMode="numeric"
                        onChange={(event) => changeQuantity(event.target.value)}
                      />
                      <button type="button" onClick={() => changeQuantity(quantity + 1)}>
                        +
                      </button>
                    </div>
                  </label>

                  <div className="store-product-detail-total">
                    {totalPrice !== null ? <strong>{currencyFormatter.format(totalPrice)}</strong> : null}
                    {totalPoints !== null ? <span>{totalPoints} pontos</span> : null}
                  </div>

                  <button type="button" className="store-product-detail-cart" onClick={addToCart}>
                    Adicionar ao carrinho
                  </button>

                  {message ? <div className="store-product-detail-message">{message}</div> : null}
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default StoreProductDetail;
